import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface LabelItem {
    name: string;
    code?: string;
    quantity: number;
    specialInstructions?: string;
}

interface LabelData {
    orderNumber: string;
    customerName: string;
    customerPhone?: string;
    createdAt: string;
    dueDate?: string;
    items: LabelItem[];
    subtotal?: number;
    vatAmount?: number;
    totalAmount: number;
    paidAmount: number;
    discountAmount?: number;
    discountCode?: string;
    loyaltyPointsEarned?: number;
    loyaltyPointsRedeemed?: number;
    paymentStatus: string;
    status: string;
    specialInstructions?: string;
    siteName?: string;
    sitePhone?: string;
    siteAddress?: string;
    itemCount: number;
    printerWidth?: string;
    showReceiptQrCode?: boolean;
    showVatInfo?: boolean;
}

interface LabelPrintProps {
    data: LabelData;
    labelType: 'garment' | 'receipt';
    onClose: () => void;
}

const LabelPrint: React.FC<LabelPrintProps> = ({ data, labelType, onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);

    const buildPrintHtml = () => {
        const content = printRef.current;
        if (!content) return null;
        return `<html><head><style>
            @page { size: 80mm auto; margin: 2mm; }
            body { font-family: 'Courier New', Courier, monospace; font-size: 12px; line-height: 1.4; color: #000; margin: 0; padding: 8px; }
            * { box-sizing: border-box; }
        </style></head><body>${content.innerHTML}</body></html>`;
    };

    const handlePrint = () => {
        try {
            const ua = navigator.userAgent.toLowerCase();
            const isAndroidApp = ua.includes('hmposapp');
            const native = (window as any).HMPosNative;

            console.log('[Print] UA:', ua);
            console.log('[Print] isAndroidApp:', isAndroidApp);
            console.log('[Print] HMPosNative:', native);
            console.log('[Print] printReceipt fn:', native?.printReceipt);

            // Strategy 1: Android App — send JSON to native Bluetooth thermal printer
            if (isAndroidApp && native?.printReceipt) {
                const printData = {
                    printerWidth: data.printerWidth || '58mm',
                    orderNumber: data.orderNumber || '',
                    customerName: data.customerName || 'Walk-in',
                    customerPhone: data.customerPhone || '',
                    createdAt: data.createdAt || '',
                    dueDate: data.dueDate || '',
                    items: (data.items || []).map(item => ({
                        name: item.name || 'Unknown',
                        code: item.code || '',
                        quantity: item.quantity || 1,
                        specialInstructions: item.specialInstructions || '',
                    })),
                    itemCount: data.itemCount || data.items?.length || 0,
                    status: data.status || 'received',
                    specialInstructions: data.specialInstructions || '',
                    // Receipt-only fields
                    siteName: data.siteName || '',
                    sitePhone: data.sitePhone || '',
                    siteAddress: data.siteAddress || '',
                    subtotal: data.subtotal || 0,
                    vatAmount: data.vatAmount || 0,
                    totalAmount: data.totalAmount || 0,
                    paidAmount: data.paidAmount || 0,
                    discountAmount: data.discountAmount || 0,
                    discountCode: data.discountCode || '',
                    loyaltyPointsEarned: data.loyaltyPointsEarned || 0,
                    loyaltyPointsRedeemed: data.loyaltyPointsRedeemed || 0,
                    paymentStatus: data.paymentStatus || 'pending',
                    showReceiptQrCode: data.showReceiptQrCode === true,
                    showVatInfo: data.showVatInfo !== false,
                };
                const jsonStr = JSON.stringify(printData);
                console.log('[Print] labelType:', labelType, 'JSON length:', jsonStr.length);

                try {
                    // Call different native method based on label type
                    const resultStr = labelType === 'garment' && native.printGarmentTag
                        ? native.printGarmentTag(jsonStr)
                        : native.printReceipt(jsonStr);

                    console.log('[Print] Native result:', resultStr);
                    if (resultStr) {
                        const result = JSON.parse(resultStr);
                        if (result.success) {
                            alert(labelType === 'garment' ? 'Garment tag sent to printer!' : 'Receipt sent to printer!');
                        } else {
                            alert('Print failed: ' + (result.message || 'Unknown error'));
                        }
                    } else {
                        alert('Print sent (no response from printer)');
                    }
                } catch (nativeErr) {
                    console.error('[Print] Native call error:', nativeErr);
                    alert('Printer error: ' + (nativeErr instanceof Error ? nativeErr.message : String(nativeErr)));
                }
                return;
            }

            // If we're in the Android app but printReceipt was not found, show debug info
            if (isAndroidApp) {
                alert('Android app detected but printer interface not found.\nHMPosNative: ' + (native ? 'exists' : 'missing') + '\nprintReceipt: ' + (native?.printReceipt ? 'exists' : 'missing'));
                return;
            }

            // For non-app platforms, use HTML-based printing
            const printContent = printRef.current;
            if (!printContent) {
                alert('Nothing to print');
                return;
            }

            const html = buildPrintHtml();
            if (!html) {
                alert('Could not generate print content');
                return;
            }

            const isAndroid = ua.includes('android');
            const isiOS = /iphone|ipad|ipod/.test(ua);
            const isMobile = isAndroid || isiOS;

            // Strategy 2: Mobile browser — open new window with auto-print
            if (isMobile) {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.open();
                    printWindow.document.write(html.replace('</body>', `
                        <script>
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 300);
                        <\/script>
                        </body>`));
                    printWindow.document.close();
                } else {
                    const blob = new Blob([html], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 10000);
                }
                return;
            }

            // Strategy 3: Desktop — use iframe-based printing
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.left = '-9999px';
            iframe.style.top = '0';
            iframe.style.width = '80mm';
            iframe.style.height = '0';
            document.body.appendChild(iframe);

            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) {
                window.print();
                document.body.removeChild(iframe);
                return;
            }

            iframeDoc.open();
            iframeDoc.write(html);
            iframeDoc.close();

            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } catch (e) {
                    console.error('iframe print error:', e);
                }
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };

            setTimeout(() => {
                try {
                    if (document.body.contains(iframe)) {
                        iframe.contentWindow?.focus();
                        iframe.contentWindow?.print();
                        setTimeout(() => {
                            if (document.body.contains(iframe)) document.body.removeChild(iframe);
                        }, 1000);
                    }
                } catch (e) { /* ignore */ }
            }, 500);
        } catch (e) {
            console.error('Print failed:', e);
            alert('Print failed: ' + (e instanceof Error ? e.message : String(e)));
        }
    };

    const formatDate = (d?: string) =>
        d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

    const formatTime = (d?: string) =>
        d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

    return (
        <>
            {/* Print-specific styles for Android Chrome window.print() fallback */}
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    .label-print-area, .label-print-area * { visibility: visible !important; }
                    .label-print-area {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 80mm !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .no-print { display: none !important; }
                    @page { size: 80mm auto; margin: 2mm; }
                }
            `}</style>

            {/* Modal overlay */}
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 no-print">
                <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {labelType === 'garment' ? 'Print Garment Tag' : 'Print Customer Receipt'}
                        </h2>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handlePrint}
                                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                🖨️ Print
                            </button>
                            <button
                                onClick={onClose}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Preview area */}
                    <div className="p-6">
                        <p className="text-xs text-gray-500 mb-4 text-center">Preview — optimised for 80mm thermal printer</p>
                        <div className="mx-auto bg-white border-2 border-dashed border-gray-300 rounded-lg overflow-hidden" style={{ maxWidth: '302px' }}>
                            <LabelContent data={data} formatDate={formatDate} formatTime={formatTime} labelType={labelType} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Print-only area */}
            <div className="label-print-area" ref={printRef} style={{ position: 'fixed', left: '-9999px', top: 0 }}>
                <LabelContent data={data} formatDate={formatDate} formatTime={formatTime} labelType={labelType} />
            </div>
        </>
    );
};

/* =========================================
   Label Content — used in both preview & print
   ========================================= */
interface LabelContentProps {
    data: LabelData;
    formatDate: (d?: string) => string;
    formatTime: (d?: string) => string;
    labelType: 'garment' | 'receipt';
}

const LabelContent: React.FC<LabelContentProps> = ({ data, formatDate, formatTime, labelType }) => {
    const labelStyle: React.CSSProperties = {
        fontFamily: "'Courier New', Courier, monospace",
        fontSize: '12px',
        lineHeight: '1.4',
        color: '#000',
        width: '100%',
        padding: '8px',
        boxSizing: 'border-box',
    };

    const hrStyle: React.CSSProperties = {
        border: 'none',
        borderTop: '1px dashed #000',
        margin: '6px 0',
    };

    const boldStyle: React.CSSProperties = {
        fontWeight: 'bold',
    };

    const centerStyle: React.CSSProperties = {
        textAlign: 'center',
    };

    return (
        <div style={labelStyle}>
            {labelType === 'garment' ? (
                /* =============================================
                    PART 1: GARMENT TAG (attach to garment)
                    ============================================= */
                <div style={{ marginBottom: '4px' }}>
                    {/* Order Number — large and prominent */}
                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '22px', margin: '6px 0 2px' }}>
                        {data.orderNumber}
                    </div>

                    {/* Customer name */}
                    <div style={{ ...centerStyle, fontSize: '14px', ...boldStyle }}>
                        {data.customerName}
                    </div>
                    <hr style={hrStyle} />

                    {/* Date info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>IN: {formatDate(data.createdAt)} {formatTime(data.createdAt)}</span>
                        <span>COLLECTION: {data.dueDate ? formatDate(data.dueDate) : 'TBC'}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Items */}
                    <div style={{ ...boldStyle, fontSize: '11px', marginBottom: '2px' }}>
                        ITEMS ({data.itemCount}):
                    </div>
                    {data.items.map((item, i) => (
                        <div key={i} style={{ fontSize: '11px', paddingLeft: '4px' }}>
                            {item.quantity}x {item.name}
                            {item.code ? ` [${item.code}]` : ''}
                            {item.specialInstructions && (
                                <div style={{ fontSize: '10px', paddingLeft: '12px', fontStyle: 'italic' }}>
                                    ⚠ {item.specialInstructions}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Special instructions */}
                    {data.specialInstructions && (
                        <>
                            <hr style={hrStyle} />
                            <div style={{ fontSize: '11px' }}>
                                <span style={boldStyle}>NOTE: </span>
                                {data.specialInstructions}
                            </div>
                        </>
                    )}

                    <hr style={hrStyle} />

                    {/* Payment status */}
                    <div style={{ ...centerStyle, fontSize: '11px' }}>
                        Payment: <span style={boldStyle}>{(data.paymentStatus || 'pending').replace('_', ' ').toUpperCase()}</span>
                    </div>
                </div>
            ) : (
                /* =============================================
                    PART 2: CUSTOMER TICKET (hand to customer)
                    ============================================= */
                <div>
                    {/* Site name / branding */}
                    {data.siteName && (
                        <div style={{ ...centerStyle, ...boldStyle, fontSize: '12px' }}>
                            {data.siteName}
                        </div>
                    )}
                    {data.siteAddress && (
                        <div style={{ ...centerStyle, fontSize: '9px' }}>
                            {data.siteAddress}
                        </div>
                    )}
                    {data.sitePhone && (
                        <div style={{ ...centerStyle, fontSize: '9px' }}>
                            Tel: {data.sitePhone}
                        </div>
                    )}

                    <hr style={hrStyle} />

                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '12px', letterSpacing: '1px' }}>
                        🧾 CUSTOMER RECEIPT
                    </div>

                    <hr style={hrStyle} />

                    {/* Order Number */}
                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '18px', margin: '4px 0' }}>
                        {data.orderNumber}
                    </div>

                    {/* Customer */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>Customer:</span>
                        <span style={boldStyle}>{data.customerName}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Date details */}
                    <div style={{ fontSize: '11px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Date In:</span>
                            <span>{formatDate(data.createdAt)} {formatTime(data.createdAt)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Collection Date:</span>
                            <span style={boldStyle}>{data.dueDate ? formatDate(data.dueDate) : 'To be confirmed'}</span>
                        </div>
                    </div>

                    <hr style={hrStyle} />

                    {/* Items list */}
                    <div style={{ ...boldStyle, fontSize: '11px', marginBottom: '2px' }}>
                        ITEMS:
                    </div>
                    {data.items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                            <span>{item.quantity}x {item.name}</span>
                        </div>
                    ))}

                    <hr style={hrStyle} />

                    {/* Payment info */}
                    <div style={{ fontSize: '11px' }}>
                        {data.subtotal !== undefined && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Subtotal:</span>
                                <span>£{(data.subtotal || 0).toFixed(2)}</span>
                            </div>
                        )}
                        {(data.discountAmount || 0) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Discount{data.discountCode ? ` (${data.discountCode})` : ''}:</span>
                                <span style={{ ...boldStyle }}>-£{(data.discountAmount || 0).toFixed(2)}</span>
                            </div>
                        )}
                        {data.showVatInfo !== false && data.vatAmount !== undefined && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>VAT:</span>
                                <span>£{(data.vatAmount || 0).toFixed(2)}</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total:</span>
                            <span style={boldStyle}>£{(data.totalAmount || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Paid:</span>
                            <span>£{(data.paidAmount || 0).toFixed(2)}</span>
                        </div>
                        {data.totalAmount - data.paidAmount > 0.01 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Balance:</span>
                                <span style={boldStyle}>£{((data.totalAmount || 0) - (data.paidAmount || 0)).toFixed(2)}</span>
                            </div>
                        )}
                        {(data.loyaltyPointsEarned || 0) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                <span>Loyalty Earned:</span>
                                <span style={boldStyle}>+{data.loyaltyPointsEarned} pts</span>
                            </div>
                        )}
                        {(data.loyaltyPointsRedeemed || 0) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Loyalty Redeemed:</span>
                                <span style={boldStyle}>{data.loyaltyPointsRedeemed} pts</span>
                            </div>
                        )}
                    </div>

                    <hr style={hrStyle} />

                    {/* Payment status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>Payment:</span>
                        <span style={boldStyle}>{(data.paymentStatus || 'pending').replace('_', ' ').toUpperCase()}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* QR Code for order lookup */}
                    {data.showReceiptQrCode && data.orderNumber && (
                        <div style={{ ...centerStyle, margin: '8px 0 4px' }}>
                            <div style={{ fontSize: '9px', marginBottom: '4px' }}>Scan to view order:</div>
                            <QRCodeSVG value={data.orderNumber} size={100} level="L" />
                        </div>
                    )}

                    <hr style={hrStyle} />

                    {/* Footer */}
                    <div style={{ ...centerStyle, fontSize: '9px', color: '#666', marginTop: '8px' }}>
                        Thank you for your business!
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabelPrint;
